/**
 * CommandPalette — Cmd/Ctrl-K launcher.
 *
 * Fuzzy-searches every app route plus a few quick actions and navigates on
 * select. Mounted once in the shell (DashboardLayout); it also registers the
 * global Cmd/Ctrl-K keydown listener that toggles it. Esc or backdrop closes.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router';

import { getFocusable, nextTrapFocus } from '@/app/keyboard-shortcuts';
import { ALL_HUB_ITEMS, getNavItem, type NavItem } from '@/app/layouts/navCatalog';
import { SECTION_TABS, findSectionTabLabel, sectionTabFullLabel } from '@/app/layouts/sectionTabs';
import { useNavAccess } from '@/app/layouts/useNavAccess';
import { useT, tr } from '@/i18n';
import { useNavStore } from '@/stores/nav-store';
import { useUIStore, type Theme } from '@/stores/ui-store';

/** Non-navigation commands run an in-app effect instead of routing. */
type CommandAction = 'toggle-theme' | 'shortcuts';

interface Command {
  id: string;
  label: string;
  /** Target route (navigation commands). Omitted for `action` commands. */
  to?: string;
  group: 'Favoris' | 'Récents' | 'Actions' | 'Navigation';
  /** Hub item this command mirrors (drives role/plan awareness + the pin star). */
  navTo?: string;
  hint?: string;
  keywords?: string;
  /** In-app effect for non-navigation commands. */
  action?: CommandAction;
}

const THEME_CYCLE: Theme[] = ['system', 'light', 'dark'];

const COMMANDS: Command[] = [
  // Quick actions first. `?new=1` is the create-intent flag (see
  // useCreateIntent): the target list page opens its create modal on land.
  { id: 'new-contact', label: tr('shell.app.commandPalette.newContact'), to: '/crm/contacts?new=1', group: 'Actions', hint: 'CRM', keywords: tr('shell.app.commandPalette.createLeadProspect') },
  { id: 'new-company', label: tr('shell.app.commandPalette.newCompany'), to: '/crm/companies?new=1', group: 'Actions', hint: 'CRM', keywords: tr('shell.app.commandPalette.createCompanyAccount') },
  { id: 'new-content', label: tr('shell.app.commandPalette.newContent'), to: '/content/new', group: 'Actions', hint: tr('shell.app.commandPalette.content'), keywords: tr('shell.app.commandPalette.createPostPublicationWrite') },
  { id: 'generate-calendar', label: tr('shell.app.commandPalette.generateAContentCalendar'), to: '/planning/generate', group: 'Actions', hint: tr('shell.app.commandPalette.content'), keywords: tr('shell.app.commandPalette.calendarGenerateSchedulePostsCadenceThem') },
  { id: 'new-project', label: tr('shell.app.commandPalette.newProject'), to: '/projects?new=1', group: 'Actions', hint: tr('shell.app.commandPalette.projects'), keywords: tr('shell.app.commandPalette.create') },
  { id: 'new-campaign', label: tr('shell.app.commandPalette.newCampaign'), to: '/outreach/campaigns?new=1', group: 'Actions', hint: tr('shell.app.commandPalette.outreach'), keywords: tr('shell.app.commandPalette.createEmailingSequence') },
  { id: 'new-design', label: tr('shell.app.commandPalette.newDesign'), to: '/design', group: 'Actions', hint: tr('shell.app.commandPalette.design'), keywords: tr('shell.app.commandPalette.createVisualGraphicImage') },
  { id: 'launch-agent', label: tr('shell.app.commandPalette.runAnAiTask'), to: '/ai-team', group: 'Actions', hint: tr('shell.app.commandPalette.ai'), keywords: tr('shell.app.commandPalette.agentPersonaLaunchRunTaskCmo') },
  { id: 'launch-orchestration', label: tr('shell.app.commandPalette.orchestrateAnAiTeamOnA'), to: '/orchestration', group: 'Actions', hint: tr('shell.app.commandPalette.ai'), keywords: tr('shell.app.commandPalette.orchestrationMultiAgentPlanDagObjective') },
  { id: 'activate-ai', label: tr('shell.app.commandPalette.enableAnAiCapability'), to: '/ai-team', group: 'Actions', hint: tr('shell.app.commandPalette.ai'), keywords: tr('shell.app.commandPalette.enableVirtualCmoContentManagerCommunity') },
  { id: 'toggle-theme', label: tr('shell.app.commandPalette.toggleTheThemeLightDark'), group: 'Actions', hint: tr('shell.app.commandPalette.display'), keywords: tr('shell.app.commandPalette.themeDarkLightAppearanceMode'), action: 'toggle-theme' },
  { id: 'shortcuts', label: tr('shell.app.commandPalette.keyboardShortcuts'), group: 'Actions', hint: tr('shell.app.commandPalette.help'), keywords: tr('shell.app.commandPalette.helpShortcutsKeysKeyboard'), action: 'shortcuts' },

  // Navigation — mirrors the sidebar and its sub-routes.
  { id: 'dashboard', label: tr('shell.app.commandPalette.dashboard'), to: '/', group: 'Navigation', keywords: tr('shell.app.commandPalette.homeDashboard') },
  { id: 'ai-ghostwriter', label: tr('shell.app.commandPalette.aiGhostwriter'), to: '/ai/ghostwriter', group: 'Navigation', keywords: tr('shell.app.commandPalette.ghostwriterWritingPostLongFormHook') },
  { id: 'analytics-monitoring', label: tr('shell.app.commandPalette.analyticsMonitoring'), to: '/analytics/monitoring', group: 'Navigation', keywords: tr('shell.app.commandPalette.monitoringAlertsAnomaliesDigestsRruleSur') },
  { id: 'crm-signals', label: tr('shell.app.commandPalette.crmSignals'), to: '/crm/signals', group: 'Navigation', keywords: tr('shell.app.commandPalette.signalsIntentBuyingIntentLeadScore') },
  { id: 'content', label: tr('shell.app.commandPalette.content'), to: '/content', group: 'Navigation', keywords: tr('shell.app.commandPalette.postsPublications') },
  { id: 'calendar', label: tr('shell.app.commandPalette.calendar'), to: '/calendar', group: 'Navigation', keywords: tr('shell.app.commandPalette.planningAgenda') },
  { id: 'planning-generate', label: tr('shell.app.commandPalette.generateACalendar'), to: '/planning/generate', group: 'Navigation', keywords: tr('shell.app.commandPalette.calendarGenerateScheduleCadenceThemesTem') },
  { id: 'planning-templates', label: tr('shell.app.commandPalette.linkedinTemplatesLeaderboard'), to: '/planning/templates', group: 'Navigation', keywords: tr('shell.app.commandPalette.templatesLeaderboardRankingPerformanceLi') },
  { id: 'planning-review', label: tr('shell.app.commandPalette.aiReviewWorkshop'), to: '/planning/review', group: 'Navigation', keywords: tr('shell.app.commandPalette.reviewExpertPanelAgenticLoopGenerate') },
  { id: 'content-engine-policy', label: tr('shell.app.commandPalette.contentEngineAutomations'), to: '/settings/content-engine', group: 'Navigation', keywords: tr('shell.app.commandPalette.automationsContentEnginePolicyLaw25') },
  { id: 'publishing', label: tr('shell.app.commandPalette.publishing'), to: '/publishing', group: 'Navigation', keywords: tr('shell.app.commandPalette.publishQueues') },
  { id: 'publishing-history', label: tr('shell.app.commandPalette.publishingHistory'), to: '/publishing/history', group: 'Navigation' },
  { id: 'platforms', label: tr('shell.app.commandPalette.platforms'), to: '/platforms', group: 'Navigation', keywords: tr('shell.app.commandPalette.networksAccountsConnections') },
  { id: 'ads', label: tr('shell.app.commandPalette.ads'), to: '/ads', group: 'Navigation', keywords: tr('shell.app.commandPalette.adsAdvertisingSponsoringPaidCampaignsBoo') },
  { id: 'ads-command', label: tr('shell.app.commandPalette.adsCommandCenter'), to: '/ads/command', group: 'Navigation', keywords: tr('shell.app.commandPalette.adsCommandCenterEngagementPreviewBilling') },
  { id: 'media', label: tr('shell.app.commandPalette.mediaLibrary'), to: '/media-library', group: 'Navigation', keywords: tr('shell.app.commandPalette.imagesVideosAssetsFiles') },
  { id: 'documents', label: tr('shell.app.commandPalette.documentsInvoices'), to: '/documents', group: 'Navigation', keywords: tr('shell.app.commandPalette.documentsInvoicesReceivedIssuedQuotesOff') },
  { id: 'bio', label: tr('shell.app.commandPalette.linkInBio'), to: '/bio', group: 'Navigation', keywords: tr('shell.app.commandPalette.bioLinkPageProfileLinktree') },
  { id: 'analytics', label: tr('shell.app.commandPalette.analytics'), to: '/analytics', group: 'Navigation', keywords: tr('shell.app.commandPalette.statisticsStatsMetrics') },
  { id: 'analytics-overview', label: tr('shell.app.commandPalette.analyticsOverview'), to: '/analytics/overview', group: 'Navigation', keywords: tr('shell.app.commandPalette.overviewSummaryInsights') },
  { id: 'analytics-hashtags', label: tr('shell.app.commandPalette.analyticsHashtags'), to: '/analytics/hashtags', group: 'Navigation', keywords: tr('shell.app.commandPalette.keywordsTrends') },
  { id: 'analytics-competitors', label: tr('shell.app.commandPalette.analyticsCompetitors'), to: '/analytics/competitors', group: 'Navigation', keywords: tr('shell.app.commandPalette.monitoringBenchmark') },
  { id: 'analytics-influencers', label: tr('shell.app.commandPalette.analyticsInfluencers'), to: '/analytics/influencers', group: 'Navigation', keywords: tr('shell.app.commandPalette.creatorsAmbassadorsInsights') },
  { id: 'analytics-funnels', label: tr('shell.app.commandPalette.analyticsFunnels'), to: '/analytics/funnels', group: 'Navigation', keywords: tr('shell.app.commandPalette.funnelsConversionStepsFunnel') },
  { id: 'analytics-cohorts', label: tr('shell.app.commandPalette.analyticsRetention'), to: '/analytics/cohorts', group: 'Navigation', keywords: tr('shell.app.commandPalette.cohortsRetentionLoyalty') },
  { id: 'analytics-attribution', label: tr('shell.app.commandPalette.analyticsAttributionRoi'), to: '/analytics/attribution', group: 'Navigation', keywords: 'attribution roi multi-touch retour investissement' },
  { id: 'analytics-goals', label: tr('shell.app.commandPalette.analyticsGoals'), to: '/analytics/goals', group: 'Navigation', keywords: tr('shell.app.commandPalette.objectivesGoalsOkrKpiTargets') },
  { id: 'analytics-reports', label: tr('shell.app.commandPalette.analyticsReports'), to: '/analytics/reports', group: 'Navigation', keywords: tr('shell.app.commandPalette.exportsInsights') },
  { id: 'analytics-install', label: tr('shell.app.commandPalette.analyticsSdkInstallation'), to: '/analytics/install', group: 'Navigation', keywords: tr('shell.app.commandPalette.sdkPixelSnippetInstallationIngestionKey') },
  { id: 'analytics-experiments', label: tr('shell.app.commandPalette.analyticsExperiments'), to: '/analytics/experiments', group: 'Navigation', keywords: tr('shell.app.commandPalette.abTestABExperimentVariants') },
  { id: 'design', label: tr('shell.app.commandPalette.design'), to: '/design', group: 'Navigation', keywords: tr('shell.app.commandPalette.graphicEditorVisuals') },
  { id: 'design-brand-kits', label: tr('shell.app.commandPalette.designBrandKits'), to: '/design/brand-kits', group: 'Navigation', keywords: tr('shell.app.commandPalette.brandKitGuidelines') },
  { id: 'workflows', label: tr('shell.app.commandPalette.workflows'), to: '/workflows', group: 'Navigation', keywords: tr('shell.app.commandPalette.automationFlowsOrchestrationScenarios') },
  { id: 'crm', label: tr('shell.app.commandPalette.crmLeads'), to: '/crm', group: 'Navigation', keywords: tr('shell.app.commandPalette.prospectingContacts') },
  { id: 'crm-contacts', label: tr('shell.app.commandPalette.crmContacts'), to: '/crm/contacts', group: 'Navigation', keywords: tr('shell.app.commandPalette.leadsProspects') },
  { id: 'crm-companies', label: tr('shell.app.commandPalette.crmCompanies'), to: '/crm/companies', group: 'Navigation', keywords: tr('shell.app.commandPalette.companiesAccounts') },
  { id: 'crm-groups', label: tr('shell.app.commandPalette.crmGroups'), to: '/crm/groups', group: 'Navigation', keywords: tr('shell.app.commandPalette.segmentsLists') },
  { id: 'crm-pipeline', label: tr('shell.app.commandPalette.crmPipeline'), to: '/crm/pipeline', group: 'Navigation', keywords: tr('shell.app.commandPalette.salesDealsOpportunitiesKanban') },
  { id: 'crm-dashboards', label: tr('shell.app.commandPalette.crmDashboards'), to: '/crm/dashboards', group: 'Navigation', keywords: tr('shell.app.commandPalette.dashboardsIndicatorsKpi') },
  { id: 'crm-follow-ups', label: tr('shell.app.commandPalette.crmFollowUps'), to: '/crm/follow-ups', group: 'Navigation', keywords: tr('shell.app.commandPalette.followUpsFollowUpsTrackingTasks') },
  { id: 'crm-reminders', label: tr('shell.app.commandPalette.crmReminders'), to: '/crm/reminders', group: 'Navigation', keywords: tr('shell.app.commandPalette.remindersAlertsDeadlines') },
  { id: 'crm-duplicates', label: tr('shell.app.commandPalette.crmDuplicates'), to: '/crm/duplicates', group: 'Navigation', keywords: tr('shell.app.commandPalette.duplicatesMergeCleanup') },
  { id: 'crm-scheduler', label: tr('shell.app.commandPalette.crmAppointments'), to: '/crm/scheduler', group: 'Navigation', keywords: tr('shell.app.commandPalette.schedulerAppointmentsBookingCalendarAgen') },
  { id: 'crm-forms', label: tr('shell.app.commandPalette.crmForms'), to: '/crm/forms', group: 'Navigation', keywords: tr('shell.app.commandPalette.formsWebFormsCaptureLeads') },
  { id: 'crm-documents', label: tr('shell.app.commandPalette.crmDocuments'), to: '/crm/documents', group: 'Navigation', keywords: tr('shell.app.commandPalette.documentsFilesContractsSignatures') },
  { id: 'crm-settings', label: tr('shell.app.commandPalette.crmSettings'), to: '/crm/settings', group: 'Navigation', keywords: tr('shell.app.commandPalette.configurationSettings') },
  { id: 'advisor-recommendations', label: tr('shell.app.commandPalette.aiRecommendations'), to: '/recommandations', group: 'Navigation', keywords: tr('shell.app.commandPalette.recommendationsAdvisorAiAnalysisSuggesti') },
  { id: 'strategy-mktg', label: tr('shell.app.commandPalette.marketingStrategy'), to: '/strategy-mktg', group: 'Navigation', keywords: tr('shell.app.commandPalette.marketingCommunicationStrategyGtmGoTo') },
  { id: 'prospection', label: tr('shell.app.commandPalette.prospectingDiscovery'), to: '/prospection', group: 'Navigation', keywords: tr('shell.app.commandPalette.discoveryGeoProspectingGooglePlacesOpenc') },
  { id: 'prospection-offers', label: tr('shell.app.commandPalette.prospectingOffersIcp'), to: '/prospection/offres', group: 'Navigation', keywords: tr('shell.app.commandPalette.offersOfferingsIcpProspectProductBrand') },
  { id: 'prospection-recommend', label: tr('shell.app.commandPalette.prospectingRecommendations'), to: '/prospection/recommend', group: 'Navigation', keywords: tr('shell.app.commandPalette.recommendationRankingFitScoreProspectsOb') },
  { id: 'prospection-calendar', label: tr('shell.app.commandPalette.prospectingCalendar'), to: '/prospection/calendar', group: 'Navigation', keywords: tr('shell.app.commandPalette.calendarCadenceMultiTouchSequenceProspec') },
  { id: 'account-intelligence', label: tr('shell.app.commandPalette.prospectingAccountIntelligence'), to: '/prospection/account-intelligence', group: 'Navigation', keywords: tr('shell.app.commandPalette.accountIntelligenceDossierAiAnalysisProf') },
  { id: 'carousel-editor', label: tr('shell.app.commandPalette.contentCarouselEditor'), to: '/content/carousel', group: 'Navigation', keywords: tr('shell.app.commandPalette.carouselSlidesExportPngPdfDesign') },
  { id: 'rich-content-studio', label: tr('shell.app.commandPalette.contentRichContentStudio'), to: '/content/studio', group: 'Navigation', keywords: tr('shell.app.commandPalette.infographicAnimatedGifMp4VideoPost') },
  { id: 'video-studio', label: tr('shell.app.commandPalette.contentVideoAudioStudio'), to: '/content/video-studio', group: 'Navigation', keywords: tr('shell.app.commandPalette.videoAudioStudioTranscriptionVoiceTts') },
  { id: 'crm-activity', label: tr('shell.app.commandPalette.crmActivityFeed'), to: '/crm/activity', group: 'Navigation', keywords: tr('shell.app.commandPalette.activityFeedCollaborationSharesAssignmen') },
  { id: 'prospect', label: tr('shell.app.commandPalette.b2bDatabase'), to: '/prospect', group: 'Navigation', keywords: tr('shell.app.commandPalette.b2bProspectingDirectoryEnrichmentCompany') },
  { id: 'prospect-search', label: tr('shell.app.commandPalette.b2bDatabaseSearch'), to: '/prospect/search', group: 'Navigation', keywords: tr('shell.app.commandPalette.b2bSearchDirectoryFiltersCompaniesContac') },
  { id: 'prospect-lists', label: tr('shell.app.commandPalette.b2bDatabaseMyLists'), to: '/prospect/lists', group: 'Navigation', keywords: tr('shell.app.commandPalette.listsBuildAListSegmentsProspecting') },
  { id: 'marketplace', label: tr('shell.app.commandPalette.marketplace'), to: '/marketplace', group: 'Navigation', keywords: tr('shell.app.commandPalette.marketplaceProvidersServicesBookings') },
  { id: 'marketplace-browse', label: tr('shell.app.commandPalette.marketplaceExplore'), to: '/marketplace/browse', group: 'Navigation', keywords: tr('shell.app.commandPalette.exploreProvidersDirectoryServices') },
  { id: 'marketplace-provider', label: tr('shell.app.commandPalette.marketplaceProviderWorkspace'), to: '/marketplace/dashboard', group: 'Navigation', keywords: tr('shell.app.commandPalette.providerSupplierDashboardServices') },
  { id: 'marketplace-bookings', label: tr('shell.app.commandPalette.marketplaceMyBookings'), to: '/marketplace/my-bookings', group: 'Navigation', keywords: tr('shell.app.commandPalette.bookingsOrders') },
  { id: 'marketplace-reviews', label: tr('shell.app.commandPalette.marketplaceMyReviews'), to: '/marketplace/my-reviews', group: 'Navigation', keywords: tr('shell.app.commandPalette.ratingsReviewsScores') },
  { id: 'social', label: tr('shell.app.commandPalette.socialLeads'), to: '/social', group: 'Navigation', keywords: tr('shell.app.commandPalette.socialLeadsSocialNetworksProspects') },
  { id: 'listening', label: tr('shell.app.commandPalette.socialListening'), to: '/listening', group: 'Navigation', keywords: tr('shell.app.commandPalette.listeningMonitoringMentionsSocialMonitor') },
  { id: 'outreach', label: tr('shell.app.commandPalette.emailOutreach'), to: '/outreach', group: 'Navigation', keywords: tr('shell.app.commandPalette.emailingCampaigns') },
  { id: 'outreach-campaigns', label: tr('shell.app.commandPalette.outreachCampaigns'), to: '/outreach/campaigns', group: 'Navigation' },
  { id: 'outreach-sequences', label: tr('shell.app.commandPalette.outreachSequences'), to: '/outreach/sequences', group: 'Navigation' },
  { id: 'outreach-unibox', label: tr('shell.app.commandPalette.outreachUnibox'), to: '/outreach/unibox', group: 'Navigation', keywords: tr('shell.app.commandPalette.uniboxUnifiedInboxRepliesConversations') },
  { id: 'outreach-sending', label: tr('shell.app.commandPalette.outreachDeliverability'), to: '/outreach/sending', group: 'Navigation', keywords: tr('shell.app.commandPalette.sendingDeliverabilityWarmupSendersDomain') },
  { id: 'outreach-templates', label: tr('shell.app.commandPalette.outreachTemplates'), to: '/outreach/templates', group: 'Navigation' },
  { id: 'outreach-settings', label: tr('shell.app.commandPalette.outreachSettings'), to: '/outreach/settings', group: 'Navigation', keywords: tr('shell.app.commandPalette.configurationSender') },
  { id: 'inbox', label: tr('shell.app.commandPalette.socialInbox'), to: '/inbox', group: 'Navigation', keywords: tr('shell.app.commandPalette.messagesConversations') },
  // Email Automation Suite
  { id: 'email-mailboxes', label: tr('shell.app.commandPalette.emailConnectedMailboxes'), to: '/email/mailboxes', group: 'Navigation', keywords: tr('shell.app.commandPalette.emailMailboxGmailOutlookYahooImap') },
  { id: 'email-inbox', label: tr('shell.app.commandPalette.emailInbox'), to: '/email/inbox', group: 'Navigation', keywords: tr('shell.app.commandPalette.emailInboxClassificationPriorityCategory') },
  { id: 'email-rules', label: tr('shell.app.commandPalette.emailRulesAutomation'), to: '/email/rules', group: 'Navigation', keywords: tr('shell.app.commandPalette.emailRulesAutomationTriagePolicyAutonomy') },
  { id: 'email-builder', label: tr('shell.app.commandPalette.emailVisualBuilder'), to: '/email/builder', group: 'Navigation', keywords: tr('shell.app.commandPalette.emailVisualBuilderAutomationFlowDrag') },
  { id: 'email-templates', label: tr('shell.app.commandPalette.emailTemplates'), to: '/email/templates', group: 'Navigation', keywords: tr('shell.app.commandPalette.emailTemplatesReplySubjectBodyVariables') },
  { id: 'community', label: tr('shell.app.commandPalette.community'), to: '/community', group: 'Navigation', keywords: tr('shell.app.commandPalette.communityProductivityRepliesMacrosSlaCsa') },
  { id: 'community-canned', label: tr('shell.app.commandPalette.communityRepliesMacros'), to: '/community/canned', group: 'Navigation', keywords: tr('shell.app.commandPalette.cannedSavedRepliesMacros') },
  { id: 'community-sla', label: tr('shell.app.commandPalette.communitySla'), to: '/community/sla', group: 'Navigation', keywords: tr('shell.app.commandPalette.slaDeadlinesPoliciesBreachesBreach') },
  { id: 'community-csat', label: tr('shell.app.commandPalette.communitySatisfactionCsat'), to: '/community/csat', group: 'Navigation', keywords: tr('shell.app.commandPalette.csatSatisfactionScoreSurvey') },
  { id: 'community-campaigns', label: tr('shell.app.commandPalette.communityInfluencerCampaigns'), to: '/community/campaigns', group: 'Navigation', keywords: tr('shell.app.commandPalette.influencerCampaignsInfluencerBriefDelive') },
  { id: 'advocacy', label: tr('shell.app.commandPalette.ambassadorsFeedToShare'), to: '/advocacy/feed', group: 'Navigation', keywords: tr('shell.app.commandPalette.advocacyEmployeeAdvocacyAmbassadorsEmplo') },
  { id: 'advocacy-leaderboard', label: tr('shell.app.commandPalette.ambassadorsLeaderboard'), to: '/advocacy/leaderboard', group: 'Navigation', keywords: 'advocacy classement leaderboard ambassadeurs opt-in partages clics' },
  { id: 'advocacy-campaigns', label: tr('shell.app.commandPalette.ambassadorsCampaignsAdmin'), to: '/advocacy/campaigns', group: 'Navigation', keywords: tr('shell.app.commandPalette.advocacyCampaignsAdminAmbassadorsInviteM') },
  { id: 'communities-engagements', label: tr('shell.app.commandPalette.communitiesMyEngagements'), to: '/communities/my-engagements', group: 'Navigation', keywords: tr('shell.app.commandPalette.communitiesAmplificationEngagementCommen') },
  { id: 'communities-groups', label: tr('shell.app.commandPalette.communitiesGroupsAdmin'), to: '/communities/groups', group: 'Navigation', keywords: tr('shell.app.commandPalette.communitiesGroupMembersInviteEditorPubli') },
  { id: 'communities-policy', label: tr('shell.app.commandPalette.communitiesPolicyCompliance'), to: '/communities/policy', group: 'Navigation', keywords: tr('shell.app.commandPalette.communitiesPolicyComplianceAutomaticOptI') },
  { id: 'communities-measurement', label: tr('shell.app.commandPalette.communitiesMeasurement'), to: '/communities/measurement', group: 'Navigation', keywords: tr('shell.app.commandPalette.communitiesMeasurementParticipationComme') },
  { id: 'inbox-approvals', label: tr('shell.app.commandPalette.inboxApprovals'), to: '/inbox/approvals', group: 'Navigation' },
  { id: 'inbox-automation', label: tr('shell.app.commandPalette.inboxAutomation'), to: '/inbox/automation', group: 'Navigation' },
  { id: 'projects', label: tr('shell.app.commandPalette.projects'), to: '/projects', group: 'Navigation', keywords: 'collaboration' },
  { id: 'ai-team', label: tr('shell.app.commandPalette.aiTeam'), to: '/ai-team', group: 'Navigation', keywords: tr('shell.app.commandPalette.agentsPersonasVirtualCmoCommunityManager') },
  { id: 'orchestration', label: tr('shell.app.commandPalette.aiOrchestration'), to: '/orchestration', group: 'Navigation', keywords: tr('shell.app.commandPalette.orchestrationTeamMultiAgentPlanDag') },
  { id: 'ai-team-builder', label: tr('shell.app.commandPalette.aiTeamBuilder'), to: '/ai-team/builder', group: 'Navigation', keywords: tr('shell.app.commandPalette.composeTeamAgentsSeatsRolesAccountant') },
  { id: 'ai-team-templates', label: tr('shell.app.commandPalette.aiTeamTemplates'), to: '/ai-team/templates', group: 'Navigation', keywords: tr('shell.app.commandPalette.templatesTeamRolesReusableInstantiatePla') },
  { id: 'ai-team-pool', label: tr('shell.app.commandPalette.aiTaskQueue'), to: '/ai-team/pool', group: 'Navigation', keywords: tr('shell.app.commandPalette.queueTasksPoolAssignClaimAgent') },
  { id: 'workflow-studio', label: tr('shell.app.commandPalette.flowStudioWorkflows'), to: '/workflows/studio', group: 'Navigation', keywords: tr('shell.app.commandPalette.studioWorkflowProcessBpmnDmnDecision') },
  { id: 'ai-assistant', label: tr('shell.app.commandPalette.aiAssistant'), to: '/ai-assistant', group: 'Navigation', keywords: tr('shell.app.commandPalette.copilotAi') },
  { id: 'ai-content-studio', label: tr('shell.app.commandPalette.aiContentStudio'), to: '/ai/content-studio', group: 'Navigation', keywords: tr('shell.app.commandPalette.carouselThreadPostGenerationStudioAi') },
  { id: 'ai-repurpose', label: tr('shell.app.commandPalette.aiRepurposing'), to: '/ai/repurpose', group: 'Navigation', keywords: tr('shell.app.commandPalette.recycleUrlArticlePostsRepurposeAi') },
  { id: 'content-ideas', label: tr('shell.app.commandPalette.contentIdeaFeed'), to: '/content/ideas', group: 'Navigation', keywords: tr('shell.app.commandPalette.ideasFeedCaptureUrlInspirationMonitoring') },
  { id: 'prospection-visitors', label: tr('shell.app.commandPalette.prospectingIdentifiedVisitors'), to: '/prospection/visitors', group: 'Navigation', keywords: tr('shell.app.commandPalette.visitorsReverseIpCompaniesWebsiteIntent') },
  { id: 'prospection-job-change', label: tr('shell.app.commandPalette.prospectingJobChangeAlerts'), to: '/prospection/job-change-alerts', group: 'Navigation', keywords: tr('shell.app.commandPalette.jobChangePositionChangeEmploymentAlerts') },
  { id: 'prospection-inbound-routers', label: tr('shell.app.commandPalette.prospectingInboundLeadRouting'), to: '/prospection/inbound-routers', group: 'Navigation', keywords: tr('shell.app.commandPalette.inboundLeadRoutingRulesAssignmentQueue') },
  { id: 'ai-longform', label: tr('shell.app.commandPalette.aiLongFormStudio'), to: '/ai/longform', group: 'Navigation', keywords: 'long-form article seo plan landing page email long brief ia' },
  { id: 'ai-canvas', label: tr('shell.app.commandPalette.aiCanvas'), to: '/ai/canvas', group: 'Navigation', keywords: tr('shell.app.commandPalette.canvasLongDocumentSectionsSidebarSelecti') },
  { id: 'ai-grid', label: tr('shell.app.commandPalette.aiGridBulkGeneration'), to: '/ai/grid', group: 'Navigation', keywords: tr('shell.app.commandPalette.gridTableBatchBulkGenerationRows') },
  { id: 'analytics-geo', label: tr('shell.app.commandPalette.analyticsGeoAiVisibility'), to: '/analytics/geo', group: 'Navigation', keywords: tr('shell.app.commandPalette.geoGenerativeEngineOptimizationVisibilit') },
  { id: 'ai-hook-library', label: tr('shell.app.commandPalette.aiHookLibrary'), to: '/ai/hook-library', group: 'Navigation', keywords: tr('shell.app.commandPalette.hooksTemplatesSwipeFewShotHooks') },
  { id: 'ai-brand-voice', label: tr('shell.app.commandPalette.aiBrandVoice'), to: '/ai/brand-voice', group: 'Navigation', keywords: tr('shell.app.commandPalette.toneStyleProfileScoreBrandVoice') },
  { id: 'ai-ab-copy', label: tr('shell.app.commandPalette.aiABCopyTests'), to: '/ai/ab-copy', group: 'Navigation', keywords: tr('shell.app.commandPalette.variantsAbTestWinnerCopyAi') },
  { id: 'ai-suggestions', label: tr('shell.app.commandPalette.aiSuggestions'), to: '/ai/suggestions', group: 'Navigation', keywords: tr('shell.app.commandPalette.recommendationsAnalyticsIdeasSuggestions') },
  { id: 'ai-personalization', label: tr('shell.app.commandPalette.aiPersonalization'), to: '/ai/personalization', group: 'Navigation', keywords: tr('shell.app.commandPalette.tokensCrmMergePersonalizationAi') },
  { id: 'ai-community', label: tr('shell.app.commandPalette.aiCommunityManager'), to: '/ai/community-ai', group: 'Navigation', keywords: tr('shell.app.commandPalette.repliesModerationCommentsDmDraftCommunit') },
  { id: 'ai-rag-documents', label: tr('shell.app.commandPalette.aiRagDocuments'), to: '/ai/rag-documents', group: 'Navigation', keywords: tr('shell.app.commandPalette.ragDocumentsSearchIndexPdfAi') },
  { id: 'ai-memory', label: tr('shell.app.commandPalette.aiMemory'), to: '/ai/memory', group: 'Navigation', keywords: tr('shell.app.commandPalette.memoryPreferencesProfileContextAi') },
  { id: 'approvals', label: tr('shell.app.commandPalette.aiApprovals'), to: '/approvals', group: 'Navigation', keywords: tr('shell.app.commandPalette.validationApprovalsAgentsActionsAi') },
  { id: 'ai-activity', label: tr('shell.app.commandPalette.aiActivity'), to: '/ai-activity', group: 'Navigation', keywords: tr('shell.app.commandPalette.agentsRunsExecutions') },
  { id: 'settings', label: tr('shell.app.commandPalette.parameters'), to: '/settings', group: 'Navigation', keywords: tr('shell.app.commandPalette.settingsConfigurationOptions') },
  { id: 'settings-profile', label: tr('shell.app.commandPalette.settingsProfile'), to: '/settings/profile', group: 'Navigation', keywords: tr('shell.app.commandPalette.themeAccount') },
  { id: 'settings-credits', label: tr('shell.app.commandPalette.settingsCredits'), to: '/settings/credits', group: 'Navigation' },
  { id: 'settings-ai-registry', label: tr('shell.app.commandPalette.settingsAiRegistry'), to: '/settings/ai-registry', group: 'Navigation' },
  { id: 'settings-connections', label: tr('shell.app.commandPalette.settingsConnections'), to: '/settings/connections', group: 'Navigation', keywords: tr('shell.app.commandPalette.integrationsAccountsNetworks') },
  { id: 'settings-review-policy', label: tr('shell.app.commandPalette.settingsReviewPolicy'), to: '/settings/review-policy', group: 'Navigation', keywords: tr('shell.app.commandPalette.reviewApprovalAutoPublishingPolicyRules') },
  // Epic 6 dashboards (listening / reporting-benchmark / influencer campaigns).
  { id: 'veille', label: tr('shell.app.commandPalette.monitoringListening'), to: '/veille', group: 'Navigation', keywords: tr('shell.app.commandPalette.monitoringListeningSocialListeningMentio') },
  { id: 'reports-benchmark', label: tr('shell.app.commandPalette.reportsBenchmark'), to: '/reports', group: 'Navigation', keywords: 'rapports reporting marque blanche white-label export pdf csv benchmark concurrents comparatif couverture' },
  { id: 'influencer-campaigns-b2b', label: tr('shell.app.commandPalette.influencerCampaigns'), to: '/influencers', group: 'Navigation', keywords: tr('shell.app.commandPalette.influencersInfluencerCampaignsShortlistP') },
];

/**
 * Resolve a pathname to the human label of the matching navigation command —
 * used by the shell breadcrumb to name the active sub-route (e.g.
 * `/crm/contacts` → "CRM · Contacts"). Prefers an exact `to` match, else the
 * deepest `to` that is a path-prefix of `pathname`. Query strings on commands
 * (e.g. `?new=1`) are ignored. Falls back to the section-tab registry (the
 * same list the palette merges) so every tab route resolves; returns `null`
 * when nothing matches.
 */
/** Display label per command group (the group value itself is an identity). */
const GROUP_LABEL_KEY = {
  Favoris: 'shell.app.commandPalette.groupFavorites',
  Récents: 'shell.app.commandPalette.groupRecent',
  Actions: 'shell.app.commandPalette.groupActions',
  Navigation: 'shell.app.commandPalette.groupNavigation',
} as const;

export function findCommandLabel(pathname: string): string | null {
  let best: Command | null = null;
  let bestLen = -1;
  for (const cmd of COMMANDS) {
    // Only navigation commands name a real page; "Actions" are create-intent
    // shortcuts (e.g. `/crm/contacts?new=1`) that would otherwise shadow the
    // page label for the same route.
    if (cmd.group !== 'Navigation' || !cmd.to) continue;
    const to = cmd.to.split('?')[0];
    if (to === pathname) return cmd.label;
    if (to !== '/' && pathname.startsWith(`${to}/`) && to.length > bestLen) {
      best = cmd;
      bestLen = to.length;
    }
  }
  // A section tab that matches exactly beats a static prefix match (the tab
  // is the more specific page); otherwise keep the deepest static prefix.
  const tabLabel = findSectionTabLabel(pathname);
  if (tabLabel && (!best || SECTION_TABS.some((t) => t.to === pathname))) return tabLabel;
  return best ? best.label : tabLabel;
}

function normalize(value: string): string {
  // Strip combining diacritics (U+0300–U+036F) so "sequences" matches "Séquences".
  return value
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase();
}

/**
 * Subsequence fuzzy score — lower is better; null when no match. Rewards
 * earlier and adjacent character matches.
 */
function fuzzyScore(query: string, text: string): number | null {
  const q = normalize(query);
  const t = normalize(text);
  if (q === '') return 0;

  let ti = 0;
  let score = 0;
  let prev = -2;
  for (const char of q) {
    let found = -1;
    for (; ti < t.length; ti += 1) {
      if (t[ti] === char) {
        found = ti;
        ti += 1;
        break;
      }
    }
    if (found === -1) return null;
    score += found;
    if (found === prev + 1) score -= 1; // adjacency bonus
    prev = found;
  }
  return score;
}

/**
 * Rank a command against the query (lower is better; null = no match).
 *
 * Tiers keep a real hit above keyword noise: a substring in the visible LABEL
 * always outranks a substring in hint/route/keywords, which always outranks a
 * loose subsequence. Before this, everything was one subsequence over the whole
 * concatenated haystack, so typing "calend" surfaced Calendrier but then smeared
 * in CRM Rappels / Analytics Hashtags / Ads because c-a-l-e-n-d happened to
 * appear scattered across their keywords.
 */
function scoreCommand(query: string, cmd: Command): number | null {
  const q = normalize(query.trim());
  if (q === '') return 0;
  const label = normalize(cmd.label);
  const hint = normalize(cmd.hint ?? '');
  const to = normalize(cmd.to ?? '');
  const keywords = normalize(cmd.keywords ?? '');

  // Tier 1 — substring of the label the user actually reads (prefix best).
  const li = label.indexOf(q);
  if (li !== -1) return li === 0 ? -1000 : -800 + li;
  // Tier 2 — substring of hint / route / keywords.
  const hi = hint.indexOf(q);
  if (hi !== -1) return -600 + hi;
  const oi = to.indexOf(q);
  if (oi !== -1) return -500 + oi;
  const ki = keywords.indexOf(q);
  if (ki !== -1) return -400 + ki;
  // Tier 3 — subsequence on the label only (typo tolerance), demoted below any
  // substring hit so keyword noise can never outrank a genuine label match.
  const fl = fuzzyScore(q, label);
  if (fl !== null) return 100 + fl;
  // Tier 4 — last-resort subsequence across the full haystack, demoted hard so
  // these scattered matches always sink beneath the real ones.
  const fh = fuzzyScore(q, `${label} ${hint} ${keywords} ${to}`);
  return fh === null ? null : 1000 + fh;
}

export function CommandPalette({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { t: tx } = useT();
  const navigate = useNavigate();
  const theme = useUIStore((s) => s.theme);
  const setTheme = useUIStore((s) => s.setTheme);
  const { t } = useT();
  const { isVisible, isLocked } = useNavAccess();
  const favorites = useNavStore((st) => st.favorites);
  const recents = useNavStore((st) => st.recents);
  const toggleFavorite = useNavStore((st) => st.toggleFavorite);
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const [showHelp, setShowHelp] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const dialogRef = useRef<HTMLDivElement>(null);

  // Global Cmd/Ctrl-K toggles the palette. Uses a ref so the listener stays
  // stable while still seeing the latest `open`.
  const openRef = useRef(open);
  openRef.current = open;
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        onOpenChange(!openRef.current);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onOpenChange]);

  // Reset + focus each time the palette opens.
  useEffect(() => {
    if (open) {
      setQuery('');
      setActiveIndex(0);
      setShowHelp(false);
      const id = window.setTimeout(() => inputRef.current?.focus(), 0);
      return () => window.clearTimeout(id);
    }
    return undefined;
  }, [open]);

  // Every hub item the static catalog above does not already name gets a
  // generated Navigation command, so the palette reaches 100% of the sidebar
  // (settings sub-pages, publishing queue/bulk/studio, ...). Then every
  // section-tab route (sentiment, audience, appels, délégation, …) that is
  // still unnamed is merged from the shared registry, so the palette truly
  // covers every app route — and each one is pinnable like a hub item.
  const allCommands = useMemo(() => {
    const known = new Set(COMMANDS.map((c) => (c.to ?? '').split('?')[0]));
    const extra: Command[] = ALL_HUB_ITEMS.filter((i) => !known.has(i.to)).map((i: NavItem) => ({
      id: `nav:${i.to}`,
      label: i.label ?? t(i.labelKey),
      to: i.to,
      group: 'Navigation' as const,
      keywords: i.to.replace(/[/-]+/g, ' '),
    }));
    for (const e of extra) known.add(e.to as string);
    const tabs: Command[] = SECTION_TABS.filter((tab) => !known.has(tab.to)).map((tab) => ({
      id: `tab:${tab.to}`,
      label: sectionTabFullLabel(tab),
      to: tab.to,
      group: 'Navigation' as const,
      keywords: `${tab.keywords ?? ''} ${tab.to.replace(/[/-]+/g, ' ')}`.trim(),
    }));
    return [...COMMANDS, ...extra, ...tabs].map((c) => {
      const navTo = c.to && c.group === 'Navigation' ? c.to.split('?')[0] : undefined;
      return navTo ? { ...c, navTo } : c;
    });
  }, [t]);

  // Role-aware: admin-only destinations are hidden for non-admins.
  const visibleCommands = useMemo(
    () =>
      allCommands.filter((c) => {
        const item = c.navTo ? getNavItem(c.navTo) : undefined;
        return !item || isVisible(item);
      }),
    [allCommands, isVisible],
  );

  const results = useMemo(() => {
    if (query.trim() === '') {
      // Empty query: Favoris then Récents on top, then the full catalog.
      const byTo = new Map<string, Command>();
      for (const c of visibleCommands) {
        if (c.navTo && !byTo.has(c.navTo)) byTo.set(c.navTo, c);
      }
      const pick = (routes: string[], group: 'Favoris' | 'Récents', skip: Set<string>): Command[] =>
        routes
          .filter((to) => byTo.has(to) && !skip.has(to))
          .map((to) => ({ ...(byTo.get(to) as Command), id: `${group}:${to}`, group }));
      const favSet = new Set(favorites);
      return [
        ...pick(favorites, 'Favoris', new Set()),
        ...pick(recents, 'Récents', favSet),
        ...visibleCommands,
      ];
    }
    return visibleCommands.map((cmd) => {
      const score = scoreCommand(query, cmd);
      return score === null ? null : { cmd, score };
    })
      .filter((x): x is { cmd: Command; score: number } => x !== null)
      .sort((a, b) => a.score - b.score)
      .map((x) => x.cmd);
  }, [query, visibleCommands, favorites, recents]);

  useEffect(() => {
    setActiveIndex(0);
    if (query.trim() !== '') setShowHelp(false);
  }, [query]);

  if (!open) return null;

  const cycleTheme = () => {
    setTheme(THEME_CYCLE[(THEME_CYCLE.indexOf(theme) + 1) % THEME_CYCLE.length]);
  };

  const select = (cmd: Command | undefined) => {
    if (!cmd) return;
    if (cmd.action === 'shortcuts') {
      setShowHelp(true);
      return;
    }
    if (cmd.action === 'toggle-theme') {
      cycleTheme();
      onOpenChange(false);
      return;
    }
    if (cmd.to) {
      onOpenChange(false);
      void navigate(cmd.to);
    }
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      // From the help view, Escape steps back to the results list first.
      if (showHelp) setShowHelp(false);
      else onOpenChange(false);
    } else if (showHelp) {
      // Help view has no list navigation.
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex((i) => (results.length === 0 ? 0 : (i + 1) % results.length));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((i) => (results.length === 0 ? 0 : (i - 1 + results.length) % results.length));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      // Defensive: activeIndex can momentarily point past a just-shrunk result
      // list (query typed then Enter before the reset effect commits). Clamp and
      // fall back to the top match so Enter always acts on a live command.
      select(results[activeIndex] ?? results[0]);
    } else if (e.key === 'Tab') {
      // Focus trap (SC 2.4.3): keep Tab cycling inside the dialog so focus can
      // never land on the backdrop'd page behind this `aria-modal` surface.
      const target = nextTrapFocus(getFocusable(dialogRef.current), document.activeElement, e.shiftKey);
      if (target) {
        e.preventDefault();
        target.focus();
      }
    }
  };

  const SHORTCUTS: { keys: string[]; label: string }[] = [
    { keys: ['⌘', 'K'], label: tx('shell.app.commandPalette.openCloseThePalette') },
    { keys: ['/'], label: tx('shell.app.commandPalette.searchOpenThePalette') },
    { keys: ['?'], label: tx('shell.app.commandPalette.keyboardShortcutsHelp') },
    { keys: ['g', '…'], label: tx('shell.app.commandPalette.goToASectionGD') },
    { keys: ['↑', '↓'], label: tx('shell.app.commandPalette.navigateTheResults') },
    { keys: ['↵'], label: tx('shell.app.commandPalette.openTheSelection') },
    { keys: ['Esc'], label: tx('shell.app.commandPalette.closeOrGoBack') },
  ];

  return (
    <div
      className="fixed inset-0 z-[10000] flex items-start justify-center bg-slate-950/60 p-4 pt-[12vh] backdrop-blur-sm"
      role="presentation"
      onMouseDown={() => onOpenChange(false)}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-label={tx('shell.app.commandPalette.commandPalette')}
        className="flex w-full max-w-xl flex-col overflow-hidden rounded-token-lg border border-border bg-surface shadow-elevation-3"
        onMouseDown={(e) => e.stopPropagation()}
        onKeyDown={onKeyDown}
      >
        <div className="flex items-center gap-2 border-b border-border px-3">
          <svg aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="h-4 w-4 text-muted">
            <circle cx="11" cy="11" r="7" strokeLinecap="round" strokeLinejoin="round" />
            <path d="m20 20-3.2-3.2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
          <input
            ref={inputRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={tx('shell.app.commandPalette.searchForAPageOrAn')}
            aria-label={tx('shell.app.commandPalette.search')}
            className="flex-1 bg-transparent py-3 text-sm text-foreground placeholder:text-muted focus:outline-none"
          />
          <kbd className="hidden shrink-0 rounded border border-border px-1.5 py-0.5 text-[10px] font-medium text-foreground sm:inline">
            Esc
          </kbd>
        </div>

        {showHelp ? (
          <div className="max-h-80 overflow-y-auto p-3">
            <div className="mb-2 flex items-center justify-between">
              <span className="text-[10px] font-semibold uppercase tracking-wide text-muted">{tx('shell.app.commandPalette.keyboardShortcuts')}</span>
              <button
                type="button"
                onClick={() => setShowHelp(false)}
                className="rounded-token px-2 py-1 text-xs text-muted hover:bg-surface-2 hover:text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
              >{tx('shell.app.commandPalette.back')}</button>
            </div>
            <ul className="space-y-1">
              {SHORTCUTS.map((s) => (
                <li
                  key={s.label}
                  className="flex items-center justify-between gap-3 rounded-token px-3 py-2 text-sm text-foreground"
                >
                  <span className="truncate">{s.label}</span>
                  <span className="flex shrink-0 items-center gap-1">
                    {s.keys.map((k) => (
                      <kbd
                        key={k}
                        className="rounded border border-border bg-surface-2 px-1.5 py-0.5 text-[10px] font-medium text-foreground"
                      >
                        {k}
                      </kbd>
                    ))}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        ) : (
        <ul className="max-h-80 overflow-y-auto p-2" role="listbox" aria-label={tx('shell.app.commandPalette.results')}>
          {results.length === 0 && (
            <li className="px-3 py-6 text-center text-sm text-muted">{tx('shell.app.commandPalette.noResults')}</li>
          )}
          {results.map((cmd, index) => {
            const active = index === activeIndex;
            const navItem = cmd.navTo ? getNavItem(cmd.navTo) : undefined;
            const locked = navItem ? isLocked(navItem) : false;
            const pinned = cmd.navTo ? favorites.includes(cmd.navTo) : false;
            const showHeader = index === 0 || results[index - 1].group !== cmd.group;
            return (
              <li key={cmd.id}>
                {showHeader && (
                  <div className="px-2 pb-1 pt-2 text-[10px] font-semibold uppercase tracking-wide text-muted">
                    {tr(GROUP_LABEL_KEY[cmd.group])}
                  </div>
                )}
                <div className="relative">
                <button
                  type="button"
                  role="option"
                  aria-selected={active}
                  onMouseEnter={() => setActiveIndex(index)}
                  onClick={() => select(cmd)}
                  className={`flex w-full items-center justify-between gap-3 rounded-token py-2 pl-3 text-left text-sm ${
                    cmd.navTo ? 'pr-10' : 'pr-3'
                  } ${active ? 'bg-primary text-primary-foreground' : 'text-foreground hover:bg-surface-2'}`}
                >
                  <span className="truncate">{cmd.label}</span>
                  {(cmd.hint || locked) && (
                    <span className={`shrink-0 text-xs ${active ? 'text-primary-foreground/80' : 'text-muted'}`}>
                      {locked ? '🔒 ' : ''}
                      {cmd.hint}
                    </span>
                  )}
                </button>
                {cmd.navTo && (
                  <button
                    type="button"
                    tabIndex={-1}
                    aria-label={pinned ? tx('shell.app.commandPalette.removeLabelFromFavorites', { label: cmd.label }) : tx('shell.app.commandPalette.addLabelToFavorites', { label: cmd.label })}
                    aria-pressed={pinned}
                    onMouseDown={(e) => e.stopPropagation()}
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleFavorite(cmd.navTo as string);
                    }}
                    className={`absolute right-1 top-1/2 flex h-7 w-7 -translate-y-1/2 items-center justify-center rounded-token text-xs focus:outline-none focus-visible:ring-2 focus-visible:ring-ring ${
                      pinned ? 'text-warning' : active ? 'text-primary-foreground/80' : 'text-muted'
                    }`}
                  >
                    {pinned ? '★' : '☆'}
                  </button>
                )}
                </div>
              </li>
            );
          })}
        </ul>
        )}
      </div>
    </div>
  );
}
